<template>
  <div>
    <error-box v-if="error" @reload="load()" />

    <loader v-else-if="loading" />

    <template v-if="!error && !loading && club">
      <template v-if="$feature.registration_fee">
        <v-teleport to="header">
          <div>
            <el-button
              icon="el-icon-tickets"
              size="medium"
              round
              @click="registrationFeePreview = true">
              {{ $tc('general.registration_fee') }}
            </el-button>
          </div>
        </v-teleport>
      </template>

      <el-row :gutter="20" class="club-panel">
        <el-col :sm="24" :md="16">
          <ClubPanel :club="club" can-edit @edit="onClubEdit($event)" />
        </el-col>
        <el-col :sm="24" :md="8">
          <panel :title="$tc('club.my_federation_information')" :loader="competitorsLoader || teamsLoader" class="info-panel">
            <data-list :list="infoData" />
          </panel>
        </el-col>
      </el-row>

      <CompetitorsPanel
        :club="club"
        :tournament="tournament"
        :competitors="tournamentCompetitors"
        :loader="competitorsLoader"
        can-edit
        @registration-fee="registrationFeePreview = true"
        @onCompetitorsChange="loadCompetitors" />

      <TeamsPanel
        :club="club"
        :tournament="tournament"
        :teams="tournamentTeams"
        :loader="teamsLoader"
        can-edit
        @registration-fee="registrationFeePreview = true"
        @onTeamsChange="loadTeams" />

      <TournamentCoachesPanel :club="club" :coaches="coaches" :tournament="tournament" can-edit />

      <TournamentRefereePanel :club="club" :referees="referees" :tournament="tournament" can-edit />

      <TournamentOfficialPanel :club="club" :officials="officials" :tournament="tournament" can-edit />
    </template>

    <RegistrationFee
      v-if="registrationFeePreview"
      :title="` - ${club.shortName}`"
      :data="tournamentCompetitors"
      :tournament="tournament"
      :team-data="tournamentTeams"
      :club="club"
      :show-club-name="false"
      @close="registrationFeePreview = null" />
  </div>
</template>

<script>
import openPrint from '@/common/print'
import registrationFeeMixin from '@/components/tournaments/registration-fee'

import ClubPanel from '@/components/clubs/ClubPanel'
import TournamentCoachesPanel from '@/components/coaches/TournamentCoachesPanel'
import TournamentRefereePanel from '@/components/referee/TournamentRefereePanel'
import TeamsPanel from '@/components/tournaments//TeamsPanel'
import TournamentOfficialPanel from '@/components/official/TournamentOfficialPanel'
import RegistrationFee from '@/components/tournaments/RegistrationFee'
import CompetitorsPanel from '@/components/tournaments//CompetitorsPanel'

export default {
  mixins: [registrationFeeMixin],

  components: {
    ClubPanel,
    TeamsPanel,
    TournamentCoachesPanel,
    TournamentRefereePanel,
    TournamentOfficialPanel,
    RegistrationFee,
    CompetitorsPanel
  },

  data () {
    return {
      openPrint,

      competitorsLoader: false,
      teamsLoader: false,

      competitors: [],
      teams: [],

      registrationFeePreview: false,

      error: false,
      loading: false
    }
  },

  computed: {
    referees () {
      return this.tournament?.referees?.filter(referee => referee.clubId === this.clubId) || []
    },

    coaches () {
      return this.tournament?.coaches?.filter(coach => coach.clubId === this.clubId) || []
    },

    officials () {
      return this.tournament?.officials?.filter(official => official.clubId === this.clubId) || []
    },

    totalCompetitors () {
      const uniqueCompetitors = new Set(this.tournamentCompetitors?.map(competitor => competitor.competitorId))
      this.tournamentTeams.forEach(team => {
        if (team.competitor) uniqueCompetitors.add(team.competitorId)
        if (team.competitorTwo) uniqueCompetitors.add(team.competitorTwoId)
        if (team.competitorThree) uniqueCompetitors.add(team.competitorThreeId)
        if (team.competitorFour) uniqueCompetitors.add(team.competitorFourId)
      })
      return uniqueCompetitors.size || 0
    },

    allData () {
      return [
        ...this.tournamentCompetitors,
        ...this.tournamentTeams.map(item => ({
          ...item,
          isTeam: true
        }))
      ]
    },

    infoData () {
      const data = [
        {
          label: this.$tc('tournament.number_of_registered_competitors'),
          text: this.totalCompetitors || '0'
        },
        {
          label: this.$tc('tournament.number_of_registered_disciplines'),
          text: this.countDisciplinesFromRegistrations(this.tournamentCompetitors) || '0'
        },
        {
          label: this.$tc('tournament.number_of_registered_team_disciplines'),
          text: this.countDisciplinesFromRegistrations(this.tournamentTeams) || '0'
        }
      ]

      if (this.$feature.registration_fee) {
        data.push({
          label: this.$tc('general.registration_fee'),
          text: this.$formatCurrency(this.registrationFee)
        })
      }

      return data
    },

    registrationFee () {
      return this.calculateFee(this.allData)
    },

    viewingActiveTournament () {
      return this.$route.name === 'ActiveTournamentClubView'
    },

    activeTournament () {
      return this.$store.state.app.activeTournament
    },

    tournamentId () {
      return this.viewingActiveTournament ? this.activeTournament?.id : this.$route.params.id
    },

    clubId () {
      return parseInt(this.$route.params.clubId)
    },

    tournament () {
      if (this.viewingActiveTournament) return this.activeTournament

      return this.$store.getters['tournaments/get'](this.tournamentId)
    },

    club () {
      return this.$store.getters['clubs/get'](this.clubId)
    },

    tournamentCompetitors () {
      return this.competitors?.filter(team => team.club?.id === this.clubId) || []
    },

    tournamentTeams () {
      return this.teams?.filter(team => team.club?.id === this.clubId) || []
    }
  },

  created () {
    this.load(!this.viewingActiveTournament)
  },

  methods: {
    calculateFee (registrations) {
      return registrations.reduce((acc, item) => {
        const priceWithTournamentDiscount = item.isTeam
          ? this.getTeamPrice(item, true)
          : this.getPrice(item, true)
        const priceWithoutTournamentDiscount = item.isTeam
          ? this.getTeamPrice(item, false)
          : this.getPrice(item, false)
        const deduction = item.club?.id
          ? (this.tournament?.clubDeductions?.find(d => parseInt(d.clubId) === parseInt(item.club.id))?.deduction || 0)
          : 0
        const itemPrice = deduction
          ? ((100 - deduction) / 100) * priceWithoutTournamentDiscount
          : priceWithTournamentDiscount
        return acc + itemPrice
      }, 0)
    },

    countDisciplinesFromRegistrations (registration) {
      return registration?.reduce((acc, item) => {
        const disc = item.categories.reduce((a, c) => a + c.disciplines.length, 0)
        return acc + disc
      }, 0)
    },

    updateClubDelete (property, item) {
      const newClub = {
        ...this.club,
        [property]: this.club[property].filter(i => i.id !== item.id)
      }

      this.$store.commit('clubs/setSingle', newClub)
    },

    updateClubAdd (property, item) {
      const newClub = {
        ...this.club,
        [property]: [...this.club[property], item]
      }

      this.$store.commit('clubs/setSingle', newClub)
    },

    async load (getTournament = true) {
      this.loading = true
      try {
        const promises = [
          this.$store.dispatch('coaches/load'),
          this.$store.dispatch('referees/load'),
          this.$store.dispatch('officials/load'),
          this.$store.dispatch('clubs/get', this.clubId),
          getTournament ? this.$store.dispatch('tournaments/get', this.tournamentId) : Promise.resolve()
        ]

        this.loadTeams()
        this.loadCompetitors()

        await Promise.all(promises)
        this.setPageData()
        this.error = false
      } catch (e) {
        console.error(e)
        this.error = true
      } finally {
        this.loading = false
      }
    },

    async loadCompetitors () {
      this.competitorsLoader = true
      try {
        const response = await this.$store.dispatch('tournaments/getCompetitors', this.tournamentId)
        this.competitors = response || []
      } catch (e) {
        console.error(e)
        this.error = true
      } finally {
        this.competitorsLoader = false
      }
    },
    async loadTeams () {
      this.teamsLoader = true
      try {
        const response = await this.$store.dispatch('tournaments/getTeams', this.tournamentId)
        this.teams = response || []
      } catch (e) {
        console.error(e)
        this.error = true
      } finally {
        this.teamsLoader = false
      }
    },

    setPageData () {
      let breadcrumbs = null

      if (this.viewingActiveTournament) {
        breadcrumbs = [
          {
            text: this.$tc('tournament.tournament', 2),
            to: { name: 'Tournaments' }
          },
          {
            text: this.tournament?.name,
            to: { name: 'ActiveTournament' }
          }
        ]
      } else {
        breadcrumbs = [
          {
            text: this.$tc('tournament.tournament', 2),
            to: { name: 'Tournaments' }
          },
          {
            text: this.tournament?.name,
            to: { name: 'TournamentView', params: { id: this.tournament.id } }
          }
        ]
      }

      breadcrumbs.push({
        text: this.club?.name
      })

      this.$store.commit('app/set', {
        title: this.tournament?.name + ' -> ' + this.club?.name,
        breadcrumbs
      })
    }
  }
}
</script>

<style lang="scss" scoped>
.club-panel {
  margin-bottom: 30px;
}

@media (max-width: 991px) {
  .club-panel {
    margin-bottom: 20px;
  }
  .info-panel {
    margin-top: 20px;
  }
}
</style>
